import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DEFAULT_REPLAY_SKIP_STEP_MS,
  DEFAULT_REPLAY_SPEED,
  clampCursor,
  playbackResumeFrom,
  type ReplaySkipStepMs,
  type ReplaySpeed,
} from '../../lib/replayPlayback'

export interface ReplayClock {
  /** Epoch milliseconds inside the hunt. */
  cursor: number
  isPlaying: boolean
  speed: ReplaySpeed
  skipStepMs: ReplaySkipStepMs
  play: () => void
  pause: () => void
  toggle: () => void
  /** Jump to an absolute position; pauses nothing, keeps playing if it was. */
  seek: (cursor: number) => void
  /** Move by one configured step. */
  skip: (direction: 1 | -1) => void
  setSpeed: (speed: ReplaySpeed) => void
  setSkipStepMs: (ms: ReplaySkipStepMs) => void
  /**
   * The live cursor, readable without a re-render. The canvas layers draw from
   * this inside their own animation frame so a trail redraw never waits on React.
   */
  cursorRef: React.MutableRefObject<number>
}

/**
 * Drives the replay cursor.
 *
 * The cursor advances from a wall-clock anchor taken when playback started rather
 * than by adding a delta each frame: accumulating per-frame deltas drifts, and at
 * 100× a few dropped frames would visibly desynchronise the clock readout from
 * the hunt's own timestamps — which is the one thing a judge reads off this
 * screen.
 */
export function useReplayClock(startsAt: number, endsAt: number): ReplayClock {
  const [cursor, setCursor] = useState(startsAt)
  const [isPlaying, setIsPlaying] = useState(false)
  const [speed, setSpeedState] = useState<ReplaySpeed>(DEFAULT_REPLAY_SPEED)
  const [skipStepMs, setSkipStepState] = useState<ReplaySkipStepMs>(DEFAULT_REPLAY_SKIP_STEP_MS)

  const cursorRef = useRef(startsAt)
  const anchorRef = useRef<{ wall: number; cursor: number } | null>(null)

  const commit = useCallback((next: number) => {
    cursorRef.current = next
    setCursor(next)
  }, [])

  // A different hunt resets playback rather than carrying a cursor that is not
  // inside the new extent.
  useEffect(() => {
    anchorRef.current = null
    setIsPlaying(false)
    setSpeedState(DEFAULT_REPLAY_SPEED)
    setSkipStepState(DEFAULT_REPLAY_SKIP_STEP_MS)
    commit(startsAt)
  }, [startsAt, endsAt, commit])

  const play = useCallback(() => {
    // Pressing play on a finished timeline replays the hunt; sitting on the end
    // doing nothing is the one behaviour that makes the button feel broken.
    const from = playbackResumeFrom(cursorRef.current, startsAt, endsAt)
    commit(from)
    anchorRef.current = null
    setIsPlaying(true)
  }, [startsAt, endsAt, commit])

  const pause = useCallback(() => {
    anchorRef.current = null
    setIsPlaying(false)
  }, [])

  const toggle = useCallback(() => {
    if (isPlaying) pause()
    else play()
  }, [isPlaying, pause, play])

  const seek = useCallback(
    (next: number) => {
      commit(clampCursor(next, startsAt, endsAt))
      // Re-anchor so playback continues from where the viewer dropped the cursor
      // instead of snapping back to the pre-drag position on the next frame.
      anchorRef.current = null
    },
    [startsAt, endsAt, commit],
  )

  const skip = useCallback(
    (direction: 1 | -1) => {
      // Offsets the ref, not a cursor read at render time, so a burst of presses
      // accumulates step for step instead of collapsing into one.
      seek(cursorRef.current + direction * skipStepMs)
    },
    [seek, skipStepMs],
  )

  const setSpeed = useCallback((next: ReplaySpeed) => {
    setSpeedState(next)
    // Re-anchor: the elapsed wall time so far was scaled by the OLD speed, and
    // reusing that anchor would jump the cursor by the ratio between them.
    anchorRef.current = null
  }, [])

  useEffect(() => {
    if (!isPlaying) return

    let frame = 0
    const step = () => {
      const now = performance.now()
      const anchor = anchorRef.current ?? { wall: now, cursor: cursorRef.current }
      anchorRef.current = anchor

      const next = anchor.cursor + (now - anchor.wall) * speed
      if (next >= endsAt) {
        // Auto-pause at the end of the hunt, exactly on the last instant.
        commit(endsAt)
        anchorRef.current = null
        setIsPlaying(false)
        return
      }
      commit(next)
      frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [isPlaying, speed, endsAt, commit])

  return {
    cursor,
    isPlaying,
    speed,
    skipStepMs,
    play,
    pause,
    toggle,
    seek,
    skip,
    setSpeed,
    setSkipStepMs: setSkipStepState,
    cursorRef,
  }
}
